import { enumMembers, type ApiSchema } from "../apiSchema";
import { readStringList } from "../content/contentDecoders";
import { readArray, readBoolean, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";
import { PERMITTED_ACTIONS, type PermittedAction } from "../permittedActions";

/**
 * One action's decision for the person (SOT-02), whichever module serves it: allowed, or refused
 * with why, what the refusal is about and, for a permission the person lacks, what would open it
 * (accounts.md GS-8). The web words each refusal code once (`refusals.<code>`) and shows a missing
 * permission with `BlockedState`; it never decides itself.
 */

/** What a refusal is about: who the person is, what must happen first, or where things are now. */
export type RefusalConcern = ApiSchema<"RefusalConcern">;
export const REFUSAL_CONCERNS = enumMembers<RefusalConcern>({ viewer: true, prerequisite: true, state: true });

/** Module A's refusals: a block, whatever anyone holds, or a permission the person lacks. */
export type AccessRefusal = ApiSchema<"Block"> | ApiSchema<"PermissionRefusal">;
export const ACCESS_REFUSALS = enumMembers<AccessRefusal>({ project_not_active: true, not_deletable: true, dmw_core: true, missing_permission: true });

/** Every module's refusal code: A's, B's review refusals and C's Run refusals. */
export type RefusalCode = AccessRefusal | ApiSchema<"ReviewRefusal"> | ApiSchema<"StepRefusal"> | ApiSchema<"ActionRefusal">;

/** What would open an action refused for a missing permission, cut to what the person may read. */
export interface AccessRemedy {
  /** The grant, as module A names it: `propose`, or `curate:<item kind>`. */
  readonly needs: string;
  /** The groups that give it, by name; none where the person may not read them. */
  readonly holders: readonly string[];
  /** Who can grant it, by name; none: an administrator. */
  readonly deciders: readonly string[];
  readonly canRequest: boolean;
}

export interface Decision<R extends RefusalCode = RefusalCode> {
  /** `null`: allowed. */
  readonly refusal: R | null;
  readonly concern: RefusalConcern | null;
  readonly remedy: AccessRemedy | null;
  /** Where asking for it asks: its project, or `null` for the installation. */
  readonly projectId: string | null;
}

export function isAllowed(decision: Decision): boolean {
  return decision.refusal === null;
}

/** An action nobody served: not offered, as one refused for where things are. */
export function unlisted(projectId: string | null): Decision<AccessRefusal> {
  return { refusal: "missing_permission", concern: "state", remedy: null, projectId };
}

export function readRemedy(value: unknown): AccessRemedy | null {
  if (value === null || value === undefined) return null;
  const remedy = readObject(value, "remedy");
  return {
    needs: readString(remedy, "needs"),
    holders: readStringList(remedy, "holders"),
    deciders: readStringList(remedy, "deciders"),
    canRequest: readBoolean(remedy, "can_request"),
  };
}

/** One served decision, its refusal one of `refusals`. */
export function readDecision<R extends RefusalCode>(action: JsonObject, refusals: readonly R[], projectId: string | null): Decision<R> {
  return {
    refusal: action["refusal"] == null ? null : readOneOf(action, "refusal", refusals),
    concern: action["concern"] == null ? null : readOneOf(action, "concern", REFUSAL_CONCERNS),
    remedy: readRemedy(action["remedy"]),
    projectId,
  };
}

/**
 * The refusal a refused call carries in its problem details (a refused `require`, a refused
 * project creation): `null` for any other problem. It can be asked for from here only where it
 * is about the installation; a project's refusal doesn't say which project.
 */
export function refusalOfProblem(details: JsonObject): Decision<AccessRefusal> | null {
  const refusal = details["refusal"];
  if (typeof refusal !== "string" || !ACCESS_REFUSALS.some((code) => code === refusal)) return null;
  const decision = readDecision(details, ACCESS_REFUSALS, null);
  if (details["resource_kind"] === "installation" || decision.remedy === null) return decision;
  return { ...decision, remedy: { ...decision.remedy, canRequest: false } };
}

/** Module A's decisions on the session's installation actions or a project's actions. */
export type AccessDecisions = ReadonlyMap<PermittedAction, Decision<AccessRefusal>>;

export function readAccessDecisions(value: unknown, projectId: string | null): AccessDecisions {
  return new Map<PermittedAction, Decision<AccessRefusal>>(readArray(value, "decisions", (item) => {
    const action = readObject(item, "decision");
    return [readOneOf(action, "action", PERMITTED_ACTIONS), readDecision(action, ACCESS_REFUSALS, projectId)] as const;
  }));
}

/** The decision on `action`; one not served isn't offered. */
export function decisionOn(decisions: AccessDecisions, action: PermittedAction, projectId: string | null): Decision<AccessRefusal> {
  return decisions.get(action) ?? unlisted(projectId);
}
